/**
 * Widget constants — shared decisions for widget data handling.
 * Window durations are per widget type: `useLastWidgetValue` takes the caller's
 * duration (default = gauge's), so other types can add their own constants.
 */

import { MachineNameAndSourceId } from "@common/types/server-list-types";

/** Event types an events-config widget can read. Hardcoded to the one type
 *  the backend supports today; the list becomes an endpoint response later,
 *  at which point only this constant changes. `name` is the dropdown label,
 *  `source_id` the value sent to the events endpoints. */
export const EVENT_TYPE_OPTIONS: MachineNameAndSourceId[] = [
  {
    name: "IMMCycleParametersEventType",
    source_id: "IMMCycleParametersEventType",
  },
];

/** Gauge widget data window: fetch the last 30 minutes (from = now − this). */
export const DURATION_GAUGE_WIDGET_MS = 30 * 60 * 1000;

/** One dropdown preset: `ms` is the persisted value, `labelKey` the suffix of
 *  the i18n key holding the wording shown to the user. */
export type DurationOption = { labelKey: string; ms: number };

/** The duration / polling dropdown wordings live with the lookback + polling
 *  fields they fill, not with the widget drawer. */
export const LOOKBACK_AND_POLLING_KEY_PREFIX =
  "modules.lookbackAndPollingFields";

/** Line chart duration dropdown options (value = ms). */
export const LINE_CHART_DURATIONS_MS = [
  { labelKey: "duration1Minute", ms: 60 * 1000 },
  { labelKey: "duration30Minutes", ms: 30 * 60 * 1000 },
  { labelKey: "duration1Hour", ms: 60 * 60 * 1000 },
  { labelKey: "duration2Hours", ms: 2 * 60 * 60 * 1000 },
  { labelKey: "duration4Hours", ms: 4 * 60 * 60 * 1000 },
  { labelKey: "duration8Hours", ms: 8 * 60 * 60 * 1000 },
  { labelKey: "duration24Hours", ms: 24 * 60 * 60 * 1000 },
  { labelKey: "duration1Week", ms: 7 * 24 * 60 * 60 * 1000 },
  { labelKey: "duration1Month", ms: 30 * 24 * 60 * 60 * 1000 },
] as const;

/** Gauge lookback dropdown — how far back we look for the last value. Smaller
 *  windows than the line chart: gauge only needs the most recent reading.
 *  Marked `(1m | 5m | 15m | 30m | 1h | 2h | 8h | 24h)`. */
export const GAUGE_LOOKBACK_MS = [
  { labelKey: "duration1Minute", ms: 60 * 1000 },
  { labelKey: "duration5Minutes", ms: 5 * 60 * 1000 },
  { labelKey: "duration15Minutes", ms: 15 * 60 * 1000 },
  { labelKey: "duration30Minutes", ms: 30 * 60 * 1000 },
  { labelKey: "duration1Hour", ms: 60 * 60 * 1000 },
  { labelKey: "duration2Hours", ms: 2 * 60 * 60 * 1000 },
  { labelKey: "duration8Hours", ms: 8 * 60 * 60 * 1000 },
  { labelKey: "duration24Hours", ms: 24 * 60 * 60 * 1000 },
] as const;

/** Default line chart window: 1 hour. */
export const LINE_CHART_DEFAULT_DURATION_MS = 60 * 60 * 1000; // ponytail: default 1h; tune when the design lands

/** ISO-8601 relative duration for the drawer presets (PT30M, PT1H, P7D, P1M). */
export const durationToIso = (ms: number): string => {
  if (ms < 60 * 60 * 1000) return `PT${Math.round(ms / 60000)}M`;
  if (ms < 24 * 60 * 60 * 1000) return `PT${Math.round(ms / 3600000)}H`;
  if (ms < 30 * 24 * 60 * 60 * 1000) return `P${Math.round(ms / 86400000)}D`;
  return `P${Math.round(ms / (30 * 24 * 60 * 60 * 1000))}M`;
};

/** Inverse of durationToIso — resolves any preset in LINE_CHART_DURATIONS_MS
 *  or GAUGE_LOOKBACK_MS. Overlap (30m/1h/2h) maps to the same ms either way. */
export const isoToDurationMs = (
  iso: string | undefined | null,
): number | null => {
  if (!iso) return null;
  return (
    LINE_CHART_DURATIONS_MS.find((d) => durationToIso(d.ms) === iso)?.ms ??
    GAUGE_LOOKBACK_MS.find((d) => durationToIso(d.ms) === iso)?.ms ??
    null
  );
};

export type LineChartFrequency =
  | "raw"
  | "quarterhourly"
  | "hourly"
  | "daily"
  | "weekly"
  | "monthly"
  | "yearly";

/** Aggregated granularity dropdown values (raw is the toggle's other side). */
export const LINE_CHART_GRANULARITIES: LineChartFrequency[] = [
  "quarterhourly",
  "hourly",
  "daily",
  "weekly",
  "monthly",
  "yearly",
];

// --- Polling --------------------------------------------------------------

/** Polling candidates for raw-mode widgets (shared dropdown list). Short-form
 *  strings — same convention as `query.sampling.polling_interval`. */
export const POLLING_INTERVALS = [
  "5s",
  "15s",
  "30s",
  "1m",
  "5m",
  "15m",
  "30m",
] as const;

/** Polling interval → suffix of the i18n key holding its dropdown wording. */
const POLLING_INTERVAL_LABEL_KEYS: Record<string, string> = {
  "5s": "polling5Seconds",
  "15s": "polling15Seconds",
  "30s": "polling30Seconds",
  "1m": "polling1Minute",
  "5m": "polling5Minutes",
  "15m": "polling15Minutes",
  "30m": "polling30Minutes",
};

/** The dropdown entry for a stored interval. `source_id` stays the short form
 *  the query persists; an interval outside `POLLING_INTERVALS` (legacy widget)
 *  keeps that short form as its label. */
export const findPollingOption = (
  interval: string,
  translate: (key: string) => string,
): MachineNameAndSourceId => {
  const labelKey = POLLING_INTERVAL_LABEL_KEYS[interval];
  return {
    name: labelKey
      ? translate(`${LOOKBACK_AND_POLLING_KEY_PREFIX}.${labelKey}`)
      : interval,
    source_id: interval,
  };
};

/** Translated dropdown options for `POLLING_INTERVALS`. */
export const buildPollingOptions = (
  translate: (key: string) => string,
): MachineNameAndSourceId[] =>
  POLLING_INTERVALS.map((interval) => findPollingOption(interval, translate));

/** Fallback when the query carries no polling_interval (legacy widgets). */
export const DEFAULT_POLLING_INTERVAL = "30s";

/** "5s" / "30s" / "1m" → milliseconds. Unparseable input → null (caller
 *  falls back to DEFAULT_POLLING_INTERVAL or skips polling). */
export const pollingToMs = (
  interval: string | null | undefined,
): number | null => {
  if (!interval) return null;
  const match = /^(\d+)([smh])$/.exec(interval.trim().toLowerCase());
  if (!match) return null;
  const unitMs = { s: 1000, m: 60_000, h: 3_600_000 }[match[2]];
  return parseInt(match[1], 10) * unitMs;
};

/** Aggregated frequency → refresh cadence = the bucket size itself. Polling
 *  faster than the bucket returns the same aggregates (re-aggregated window);
 *  polling at the bucket size picks up each new closed bucket. */
export const AGGREGATED_POLLING_MS: Record<string, number> = {
  quarterhourly: 15 * 60 * 1000,
  hourly: 60 * 60 * 1000,
  daily: 24 * 60 * 60 * 1000,
  weekly: 7 * 24 * 60 * 60 * 1000,
  monthly: 30 * 24 * 60 * 60 * 1000,
  yearly: 365 * 24 * 60 * 60 * 1000,
};

/** Label per aggregated frequency — the locked polling display shows the
 *  effective refresh cadence (which equals the bucket size). */
export const AGGREGATED_POLLING_LABELS: Record<string, string> = {
  quarterhourly: "15m",
  hourly: "1h",
  daily: "24h",
  weekly: "7d",
  monthly: "30d",
  yearly: "1y",
};

/** Resolve the refresh interval for a widget query. Raw mode uses the
 *  user-picked polling_interval; aggregated uses the bucket cadence. */
export const resolvePollingMs = (
  frequency: string | null | undefined,
  pollingInterval: string | null | undefined,
): number | null => {
  if (!frequency || frequency === "raw") {
    return (
      pollingToMs(pollingInterval) ?? pollingToMs(DEFAULT_POLLING_INTERVAL)
    );
  }
  return AGGREGATED_POLLING_MS[frequency] ?? null;
};
